import { Check, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { validateForPublish } from '../lib/templateHelpers'
import type { PublishMode, SablonSurum } from '../types'
import { PersonelPicker } from './PersonelPicker'

export function PublishSidebar({
  surum,
  versionLabel,
  changeReason,
  onChangeReason,
  mode,
  onModeChange,
  employeeIds,
  onEmployeeIdsChange,
  showVersionInput,
}: {
  surum: SablonSurum
  versionLabel: string
  changeReason: string
  onChangeReason: (v: string) => void
  mode: PublishMode
  onModeChange: (m: PublishMode) => void
  employeeIds: string[]
  onEmployeeIdsChange: (ids: string[]) => void
  showVersionInput?: boolean
}) {
  const { t } = useTranslation()
  const errors = validateForPublish(surum)
  const checks = [
    { ok: surum.method === 'sistem' || !!surum.uploadedFile, key: 'file' },
    { ok: surum.fields.some((f) => f.required) || surum.method === 'pdf', key: 'required' },
    { ok: !errors.includes('undefined_vars'), key: 'mapped' },
    { ok: !errors.includes('required_mapping'), key: 'autoConfigured' },
    { ok: surum.sections.length > 0 || surum.method !== 'sistem', key: 'structure' },
  ]

  return (
    <div className="space-y-4 rounded-2xl border border-border/70 bg-card p-4 shadow-xs">
      <div>
        <h3 className="text-sm font-semibold">{t('hr.belgeler.publish.versionInfo')}</h3>
        {showVersionInput ? (
          <div className="mt-2 space-y-1.5">
            <Label htmlFor="ver">{t('hr.belgeler.publish.version')}</Label>
            <Input id="ver" value={versionLabel} readOnly />
          </div>
        ) : null}
        <div className="mt-2 space-y-1.5">
          <Label htmlFor="reason">{t('hr.belgeler.publish.changeReason')}</Label>
          <Textarea
            id="reason"
            rows={3}
            maxLength={500}
            value={changeReason}
            onChange={(e) => onChangeReason(e.target.value)}
            placeholder={t('hr.belgeler.publish.changeReasonPh')}
          />
          <div className="text-right text-[11px] text-muted-foreground">{changeReason.length}/500</div>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('hr.belgeler.publish.options')}</h3>
        <div className="space-y-2">
          {(
            [
              ['future_only', 'hr.belgeler.publish.futureOnly'],
              ['all_matching', 'hr.belgeler.publish.allMatching'],
              ['selected_employees', 'hr.belgeler.publish.selectedEmployees'],
            ] as const
          ).map(([id, key]) => (
            <label
              key={id}
              className={cn(
                'flex cursor-pointer gap-2 rounded-xl border px-3 py-2.5 text-sm',
                mode === id ? 'border-primary bg-primary/5' : 'border-border/70',
              )}
            >
              <input
                type="radio"
                name="publish-mode"
                checked={mode === id}
                onChange={() => onModeChange(id)}
                className="mt-0.5 accent-primary"
              />
              <span>{t(key)}</span>
            </label>
          ))}
        </div>
        {mode === 'selected_employees' ? (
          <div className="mt-3">
            <PersonelPicker selectedIds={employeeIds} onChange={onEmployeeIdsChange} />
          </div>
        ) : null}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold">{t('hr.belgeler.publish.checklist')}</h3>
        <ul className="space-y-1.5">
          {checks.map((c) => (
            <li key={c.key} className="flex items-center gap-2 text-sm">
              <span
                className={cn(
                  'flex size-5 items-center justify-center rounded-full',
                  c.ok ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground',
                )}
              >
                <Check className="size-3" />
              </span>
              {t(`hr.belgeler.publish.check.${c.key}`)}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-2 rounded-xl border border-info/25 bg-info/8 px-3 py-2.5 text-xs">
        <Info className="mt-0.5 size-3.5 shrink-0 text-info" />
        <p>{t('hr.belgeler.publish.preserveNote')}</p>
      </div>
    </div>
  )
}
